import { useState, type JSX } from 'react'
import { Button, Command } from '@ttab/elephant-ui'
import { SlidersHorizontalIcon } from '@ttab/elephant-ui/icons'
import { DebouncedCommandInput } from '@/components/Commands/Menu/DebouncedCommandInput'
import { useQuery, type QueryParams } from '@/hooks/useQuery'
import { QuickFilter } from '@/components/Table/QuickFilter'
import { useTranslation } from 'react-i18next'
import { AdvancedSearchDialog, AdvancedSearchBadges, factboxFields } from '@/components/AdvancedSearch'
import { useAdvancedSearchParams } from '@/components/AdvancedSearch/hooks/useAdvancedSearchParams'
import { useAutoDocumentOrigin } from './hooks/useAutoDocumentOrigin'
import { ArticleSearchNotice } from './ArticleSearchNotice'

// Its own component so that only the input, not the whole toolbar, re-renders with
// the table context that useAutoDocumentOrigin reads.
const FreeTextSearch = ({ filter, setFilter, placeholder }: {
  filter: QueryParams
  setFilter: (params: QueryParams) => void
  placeholder: string
}): JSX.Element => {
  const applyOrigin = useAutoDocumentOrigin()

  return (
    <Command className='w-[200px] md:w-[300px] lg:w-[400px] [&_[cmdk-input-wrapper]]:border-none'>
      <DebouncedCommandInput
        value={typeof filter.query === 'string' ? filter.query : filter.query?.[0]}
        onChange={(value: string | undefined) => {
          applyOrigin(value ?? '')
          if (value) {
            setFilter({ query: [value] })
          } else {
            setFilter({})
          }
        }}
        placeholder={placeholder}
        className='h-9'
      />
    </Command>
  )
}

export const Toolbar = (): JSX.Element => {
  const [filter, setFilter] = useQuery()
  const { t } = useTranslation('shared')
  const [dialogOpen, setDialogOpen] = useState(false)

  const {
    state: advancedState,
    isAdvancedActive,
    applyAdvancedSearch,
    clearAdvancedSearch
  } = useAdvancedSearchParams(filter, factboxFields, setFilter)

  return (
    <div className='bg-background flex items-center justify-between py-1 px-4 border-b sticky top-0 z-10'>
      <div className='flex items-center gap-1 min-w-0'>
        <Button
          variant='ghost'
          size='xs'
          title={t('common:advancedSearch.title')}
          onClick={() => setDialogOpen(true)}
          className='h-9 w-9 shrink-0'
        >
          <SlidersHorizontalIcon size={18} strokeWidth={1.75} />
        </Button>

        {!isAdvancedActive && (
          <FreeTextSearch
            filter={filter}
            setFilter={setFilter}
            placeholder={t('toolbar.freeTextSearch')}
          />
        )}

        {!isAdvancedActive && (
          <ArticleSearchNotice
            text={filter.query?.toString() ?? ''}
            origin={filter.documentOrigin?.toString()}
          />
        )}

        {isAdvancedActive && (
          <AdvancedSearchBadges
            state={advancedState}
            fields={factboxFields}
            onEdit={() => setDialogOpen(true)}
            onClear={clearAdvancedSearch}
          />
        )}
      </div>

      <AdvancedSearchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        fields={factboxFields}
        state={advancedState}
        onApply={applyAdvancedSearch}
        onClear={clearAdvancedSearch}
      />

      <div className='shrink-0'>
        <QuickFilter />
      </div>
    </div>
  )
}
